import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import audioService from '@/services/audioService';
import api from '@/services/api';
import userManager from '@/services/userManager';
import { calculateTestVolume, calculateReliabilityScore, calculateAverageThreshold, interpretHearingLoss } from '@/services/audioCalibration';
import { Volume2, Headphones, AlertCircle, FileText, Activity, CheckCircle, ChevronLeft } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
// AudiometricTestUpload removed for GDPR/HDS compliance

const FREQUENCIES = [125, 250, 500, 1000, 2000, 4000, 8000];
const MIN_DB = 0; // Start at 0 dB HL (hearing threshold)
const MAX_DB = 120; // Profound hearing loss
const DB_STEP = 5;
const AUTO_INCREMENT_DELAY = 3000; // 3 seconds between automatic increases

const AudiometricTest = ({ t }) => {
  const navigate = useNavigate();
  const [testMode, setTestMode] = useState(null); // null = selection, 'manual' = manual test
  const [currentFreqIndex, setCurrentFreqIndex] = useState(0);
  const [currentEar, setCurrentEar] = useState('left'); // Start with left ear
  const [currentDb, setCurrentDb] = useState(MIN_DB);
  const [leftResults, setLeftResults] = useState([]);
  const [rightResults, setRightResults] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [masterVolume, setMasterVolume] = useState(90); // Volume slider at 90%
  const autoIncrementTimer = useRef(null);
  const currentDbRef = useRef(MIN_DB); // Track current dB with ref to avoid double triggers
  
  // Load calibration data from localStorage with fallback
  const calibrationData = useRef({ offset: 0 }); // Default fallback: no offset
  
  useEffect(() => {
    // Calibration supprimée : test audio lancé avec offset neutre
    calibrationData.current = { offset: 0 };
  }, []);

  const currentFreq = FREQUENCIES[currentFreqIndex];
  // Progress: each frequency tested on both ears = 2 tests per frequency
  const totalTests = FREQUENCIES.length * 2;
  const completedTests = (currentFreqIndex * 2) + (currentEar === 'right' ? 1 : 0);
  const progress = (completedTests / totalTests) * 100;

  useEffect(() => {
    return () => {
      audioService.stopTone();
      if (autoIncrementTimer.current) {
        clearTimeout(autoIncrementTimer.current);
      }
    };
  }, []);

  const startTest = () => {
    setIsTesting(true);
    setCurrentFreqIndex(0);
    setCurrentEar('left');
    setCurrentDb(MIN_DB);
    currentDbRef.current = MIN_DB; // Sync ref
    playCurrentTone();
  };

  const playCurrentTone = (earToPlay = currentEar, freqToPlay = null) => {
    // Use explicit frequency if provided, otherwise use currentFreq
    const frequency = freqToPlay !== null ? freqToPlay : currentFreq;
    
    
    // Use ISO-based calculation with calibration offset
    const calibrationOffset = calibrationData.current ? calibrationData.current.offset : 0;
    const finalVolume = calculateTestVolume(currentDbRef.current, frequency, calibrationOffset);
    
    // Validate volume
    if (isNaN(finalVolume) || finalVolume === undefined || finalVolume === null) {
      console.error(`❌ CRITICAL: Invalid volume calculated: ${finalVolume} at ${currentDbRef.current} dB HL`);
      console.error(`   Calibration offset: ${calibrationOffset}, frequency: ${frequency}`);
      // Use emergency fallback
      const emergencyVolume = Math.pow(10, (currentDbRef.current - 60) / 40) * 0.01;
      audioService.playTone(frequency, Math.min(emergencyVolume, 0.5), earToPlay);
    } else {
      audioService.playTone(frequency, finalVolume, earToPlay, { pulse: true });
    }
    
    setIsPlaying(true);
    
    // Clear any existing timer
    if (autoIncrementTimer.current) {
      clearTimeout(autoIncrementTimer.current);
    }
    
    // Start auto-increment timer - PASS earToPlay and frequency!
    autoIncrementTimer.current = setTimeout(() => {
      autoIncrementVolume(earToPlay, frequency);
    }, AUTO_INCREMENT_DELAY);
  };

  const autoIncrementVolume = (earToPlay = currentEar, freqToPlay = null) => {
    // Use explicit frequency if provided, otherwise use currentFreq
    const frequency = freqToPlay !== null ? freqToPlay : currentFreq;
    
    // Increment the ref value
    currentDbRef.current += DB_STEP;
    const newDb = currentDbRef.current;
    
    // Update state for UI display
    setCurrentDb(newDb);
    
    if (newDb > MAX_DB) {
      // Max reached, move to next
      setTimeout(() => moveToNext(MAX_DB), 500);
      return;
    }
    
    console.log(`📈 AUTO INCREMENT: newDb=${newDb}, earToPlay="${earToPlay}", freq=${frequency}Hz`);
    
    // CRITICAL: Stop current tone completely
    audioService.stopTone();
    
    // Wait briefly for complete cleanup before playing new tone
    setTimeout(() => {
      // Use ISO-based calculation
      const calibrationOffset = calibrationData.current ? calibrationData.current.offset : 0;
      const finalVolume = calculateTestVolume(newDb, frequency, calibrationOffset);
      
      // Validate volume
      if (isNaN(finalVolume) || finalVolume === undefined || finalVolume === null) {
        console.error(`❌ CRITICAL: Invalid volume in auto-increment: ${finalVolume} at ${newDb} dB HL`);
        // Use emergency fallback
        const emergencyVolume = Math.pow(10, (newDb - 60) / 40) * 0.01;
        audioService.playTone(frequency, Math.min(emergencyVolume, 0.5), earToPlay);
      } else {
        audioService.playTone(frequency, finalVolume, earToPlay, { pulse: true });
      }
      
      // Schedule next increment only if not at max
      if (newDb < MAX_DB) {
        if (autoIncrementTimer.current) {
          clearTimeout(autoIncrementTimer.current);
        }
        autoIncrementTimer.current = setTimeout(() => {
          autoIncrementVolume(earToPlay, frequency);
        }, AUTO_INCREMENT_DELAY);
      } else if (newDb === MAX_DB) {
        // Reached MAX_DB (120 dB HL) - wait for user click or auto-advance after delay
        if (autoIncrementTimer.current) {
          clearTimeout(autoIncrementTimer.current);
        }
        // Give user 5 seconds to respond at max volume, then auto-advance
        autoIncrementTimer.current = setTimeout(() => {
          moveToNext(MAX_DB, earToPlay); // Pass earToPlay to avoid closure issue
        }, 5000); // 5 seconds at max volume
      }
    }, 100);
  };

  const handleHeard = () => {
    // Clear auto-increment timer immediately
    if (autoIncrementTimer.current) {
      clearTimeout(autoIncrementTimer.current);
      autoIncrementTimer.current = null;
    }
    
    audioService.stopTone();
    setIsPlaying(false);

    moveToNext(currentDb);
  };

  const handleStopTest = () => {
    // Clear auto-increment timer
    if (autoIncrementTimer.current) {
      clearTimeout(autoIncrementTimer.current);
      autoIncrementTimer.current = null;
    }
    
    // Stop all audio
    audioService.stopTone();
    
    // Reset all states to initial values
    setIsPlaying(false);
    setIsTesting(false);
    setTestComplete(false);
    setCurrentFreqIndex(0);
    setCurrentEar('left');
    setCurrentDb(MIN_DB);
    setLeftResults([]);
    setRightResults([]);
  };

  const moveToNext = (thresholdDb, explicitEar = null) => {
    // Clear any existing timer
    if (autoIncrementTimer.current) {
      clearTimeout(autoIncrementTimer.current);
      autoIncrementTimer.current = null;
    }
    
    // Use explicit ear if provided, otherwise use current state
    const earToUse = explicitEar || currentEar;
    
    const result = {
      frequency: currentFreq,
      threshold: thresholdDb
    };

    console.log(`📊 MOVE TO NEXT: explicitEar="${explicitEar}", currentEar="${currentEar}", earToUse="${earToUse}", currentFreqIndex=${currentFreqIndex}, freq=${currentFreq}Hz, threshold=${thresholdDb}dB`);
    console.log(`   Total frequencies: ${FREQUENCIES.length}, Current frequency: ${FREQUENCIES[currentFreqIndex]}`);

    // Save result for current ear
    if (earToUse === 'left') {
      setLeftResults(prev => {
        const newResults = [...prev, result];
        console.log(`   Left results now:`, newResults);
        return newResults;
      });
      // Switch to right ear for same frequency
      setCurrentEar('right');
      setCurrentDb(MIN_DB);
      currentDbRef.current = MIN_DB; // Reset ref
      // Stop any playing sound
      audioService.stopTone();
      setIsPlaying(false);
      // CRITICAL FIX: Pass currentFreq explicitly since it won't change for right ear
      setTimeout(() => {
        playCurrentTone('right', currentFreq);
      }, 500);
    } else {
      // Right ear done, save result
      setRightResults(prev => {
        const newResults = [...prev, result];
        console.log(`   Right results now:`, newResults);
        return newResults;
      });
      
      // Move to next frequency
      if (currentFreqIndex < FREQUENCIES.length - 1) {
        // CRITICAL FIX: Calculate next frequency BEFORE setState
        const nextFreqIndex = currentFreqIndex + 1;
        const nextFrequency = FREQUENCIES[nextFreqIndex];
        
        setCurrentFreqIndex(nextFreqIndex);
        setCurrentEar('left'); // Start with left ear for next frequency
        setCurrentDb(MIN_DB);
        currentDbRef.current = MIN_DB; // Reset ref
        // Stop any playing sound
        audioService.stopTone();
        setIsPlaying(false);
        // CRITICAL FIX: Pass nextFrequency explicitly to avoid async state issue
        setTimeout(() => {
          playCurrentTone('left', nextFrequency);
        }, 500);
      } else {
        // All done
        console.log(`🎉 Test complete! All ${FREQUENCIES.length} frequencies tested for both ears`);
        if (autoIncrementTimer.current) {
          clearTimeout(autoIncrementTimer.current);
          autoIncrementTimer.current = null;
        }
        audioService.stopTone();
        setIsPlaying(false);
        setIsTesting(false);
        setTestComplete(true);
      }
    }
  };

  const saveResults = async () => {
    try {
      const userId = await userManager.getUserId();
      
      // Calculate reliability score
      const leftThresholds = leftResults.map(r => r.threshold);
      const rightThresholds = rightResults.map(r => r.threshold);
      const reliability = calculateReliabilityScore(leftThresholds, rightThresholds, FREQUENCIES);
      
      console.log('📊 Reliability score:', reliability);

      console.log('[AUDIO TEST][SAVE] leftResults =', leftResults);
      console.log('[AUDIO TEST][SAVE] rightResults =', rightResults);
      console.log('[AUDIO TEST][SAVE] left len =', leftResults.length);
      console.log('[AUDIO TEST][SAVE] right len =', rightResults.length);
      console.log('[AUDIO TEST][SAVE] snapshot', {
        leftResultsLength: leftResults.length,
        rightResultsLength: rightResults.length,
        leftResults,
        rightResults
      });

      if (leftResults.length > 0) {
  console.log('[AUDIO TEST][SAVE] creating LEFT test', {
    userId,
    ear: 'left',
    resultsCount: leftResults.length,
    results: leftResults
  });

  const leftResponse = await api.createAudiometricTest({
    user_id: userId,
    ear: 'left',
    results: leftResults,
    reliability_score: reliability.score
  });

  console.log('[AUDIO TEST][SAVE] LEFT response =', leftResponse);
}

if (rightResults.length > 0) {
  console.log('[AUDIO TEST][SAVE] creating RIGHT test', {
    userId,
    ear: 'right',
    resultsCount: rightResults.length,
    results: rightResults
  });

  const rightResponse = await api.createAudiometricTest({
    user_id: userId,
    ear: 'right',
    results: rightResults,
    reliability_score: reliability.score
  });

  console.log('[AUDIO TEST][SAVE] RIGHT response =', rightResponse);
}

      navigate('/history');
    } catch (error) {
      console.error('Error saving results:', error);
    }
  };

  if (testComplete) {
  const isFr = !t?.locale || t?.locale !== 'en-US';
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6">
      <div className="max-w-sm mx-auto text-center space-y-8">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
          <CheckCircle className="h-7 w-7 text-green-400" />
        </div>

        <div className="space-y-3">
          <h1 className="text-white text-xl font-medium">
            {isFr ? 'Hearen s\'est ajusté pour toi' : 'Hearen has adjusted to you'}
          </h1>
          <p className="text-white/60 text-sm">
            {isFr ? 'Tu peux commencer' : 'You can start'}
          </p>
        </div>

        <div
          className="text-center px-4 py-3 rounded-xl"
          style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}
        >
          <p className="text-white/60 text-xs font-medium">
            {isFr
              ? 'Tu peux maintenant ajuster le volume à un niveau confortable'
              : 'You can now adjust your volume to a comfortable level'}
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={saveResults}
            className="flex-1 rounded-xl py-3"
            style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)' }}
            size="lg"
            data-testid="save-results-btn"
          >
            {isFr ? 'Sauvegarder' : 'Save'}
          </Button>
          <Button
            onClick={() => {
              audioService.stopTone();
              navigate('/');
            }}
            className="flex-1 rounded-xl py-3"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}
            size="lg"
          >
            {isFr ? 'Accueil' : 'Home'}
          </Button>
        </div>
      </div>
    </div>
  );
}

  if (isTesting) {
    const isFr = !t?.locale || t?.locale !== 'en-US';
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6">
        <div className="max-w-sm mx-auto space-y-6">
          <Progress value={progress} className="w-full" />

          {/* Ear Indicator */}
          <div className="flex justify-center items-center gap-8">
            <div className="flex flex-col items-center">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${currentEar === 'left' ? 'bg-blue-500/40 border border-blue-400/50' : 'bg-white/5'}`}>
                <span className={`font-medium text-sm ${currentEar === 'left' ? 'text-blue-300' : 'text-white/20'}`}>L</span>
              </div>
              <span className="text-white/50 text-xs mt-1">{isFr ? 'Gauche' : 'Left'}</span>
            </div>
            <div className="flex flex-col items-center">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${currentEar === 'right' ? 'bg-red-500/40 border border-red-400/50' : 'bg-white/5'}`}>
                <span className={`font-medium text-sm ${currentEar === 'right' ? 'text-red-300' : 'text-white/20'}`}>R</span>
              </div>
              <span className="text-white/50 text-xs mt-1">{isFr ? 'Droite' : 'Right'}</span>
            </div>
          </div>

          {/* Audio visual — cleaner and more alive */}
          <div className="flex justify-center">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 rounded-full bg-indigo-500/10 animate-hearenPulse" />
              <div className="absolute inset-2 rounded-[45%_55%_60%_40%/60%_40%_60%_40%] bg-indigo-400/40 animate-hearenMorph" />
              <div className="absolute inset-6 rounded-full bg-indigo-300/60" />
           </div>
          </div>

          <p className="text-white/60 text-sm text-center font-medium">
            {isFr ? 'Appuie dès que tu entends le son' : 'Tap as soon as you hear the sound'}
          </p>

          {/* Dominant "J'entends" + subtle Stop */}
          <div className="flex gap-3">
            <Button
              onClick={handleHeard}
              className="flex-[2] rounded-xl py-5 text-base font-semibold"
              style={{ background: 'rgba(34,197,94,0.35)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)', boxShadow: '0 0 20px rgba(34,197,94,0.1)' }}
              size="lg"
              data-testid="i-hear-btn"
            >
              {isFr ? 'J\'entends' : 'I hear it'}
            </Button>
            <Button
              onClick={handleStopTest}
              className="flex-1 rounded-xl py-5 text-sm"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid="stop-test-btn"
            >
              Stop
            </Button>
          </div>

          <p className="text-white/60 text-xs text-center">
            {isFr ? 'Tout reste dans une zone confortable' : 'Everything stays in a comfortable range'}
          </p>
        </div>
      </div>
    );
  }

  // Mode selection screen — V1.1.2: Simplified, non-technical
  if (testMode === null) {
    const isFr = !t?.locale || t?.locale !== 'en-US';
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6">
        <div className="max-w-sm mx-auto text-center space-y-8">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
            <Activity className="h-7 w-7 text-indigo-400" />
          </div>

          <div className="space-y-3">
            <h1 className="text-white text-xl font-medium">
              {isFr ? 'Explorer ton écoute' : 'Explore your hearing'}
            </h1>
            <p className="text-white/60 text-sm">
              {isFr ? 'On ajuste progressivement pour ton confort' : 'We gently adjust for your comfort'}
            </p>
          </div>

          <Button
            onClick={() => setTestMode('manual')}
            className="w-full rounded-xl py-3"
            style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)' }}
            size="lg"
            data-testid="start-explore-btn"
          >
            {isFr ? 'Commencer' : 'Begin'}
          </Button>

          <p className="text-white/60 text-xs">
            {isFr ? 'Tout reste dans une zone confortable' : 'Everything stays in a comfortable range'}
          </p>
        </div>
      </div>
    );
  }

  // Manual Test Mode — V1.1.2: Simplified pre-start screen
  const isFr = !t?.locale || t?.locale !== 'en-US';
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6">
      <div className="max-w-sm mx-auto space-y-8">
        <button
          onClick={() => {
            audioService.stopTone();
            setTestMode(null);
            setIsTesting(false);
            setTestComplete(false);
          }}
          className="p-2 rounded-lg transition-colors"
          style={{ background: 'rgba(255,255,255,0.05)' }}
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5 text-white/40" />
        </button>

        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
            <Headphones className="h-7 w-7 text-indigo-400" />
          </div>
          <h1 className="text-white text-xl font-medium">
            {isFr ? 'Explorer ton écoute' : 'Explore your hearing'}
          </h1>
          <p className="text-white/60 text-sm">
            {isFr ? 'Écoute simplement le son' : 'Just listen to the sound'}
          </p>
          <p className="text-white/50 text-sm">
            {isFr ? 'Appuie dès que tu entends' : 'Tap when you hear it'}
          </p>
        </div>


        {/* Volume message */}
        <div
          className="text-center px-4 py-3 rounded-xl mx-auto mt-4"
          style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}
        >
          <p className="text-white/60 text-xs font-medium">
            {isFr ? 'Mets le volume de ton appareil au maximum' : 'Set your device volume to maximum'}
          </p>
          <p className="text-white/50 text-[11px] mt-0.5">
            {isFr ? 'Hearen ajuste ensuite pour toi' : 'Hearen will adjust it for you'}
          </p>
        </div>

        <Button
          onClick={startTest}
          className="w-full rounded-xl py-3"
          style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)' }}
          size="lg"
          data-testid="start-test-btn"
        >
          {isFr ? 'Commencer' : 'Begin'}
        </Button>

        <p className="text-white/60 text-xs text-center">
          {isFr ? 'Tout reste dans une zone confortable' : 'Everything stays in a comfortable range'}
        </p>
      </div>
    </div>
  );
};

export default AudiometricTest;